///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, UrlSerializer } from '@angular/router';
import { UserSessionRestoringDTO } from '../../../generated/shared-types.model';
import { distinct, filter, map, switchMap, toArray } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { GtthLocalStorageService } from '../localStorage.service';
import { GtthSessionNavigationModel } from './session-navigation.model';
import { PARAM_CUSTOMER_ID, PARAM_PRODUCT_TYPE } from '../../constants/url-params.constants';
import { UserSessionRestoringContentComponent } from './notification-content/user-session-restoring-content.component';
import { SuiNotificationService, SuiNotificationType } from '@siklu/sui-lib/infrastructure';
import { isDefined } from '@siklu/sui-lib/utils';

@Injectable({ providedIn: 'root' })
export class GtthUserSessionRestoringService {
    private readonly translationPrefix = 'userSessionRestoring';
    private readonly API_URL = 'api/account/session-info';
    private readonly projectViewUrl = '/project-view/';

    private notificationId: number;

    private navigationCommandHistory = '';

    constructor(
        private readonly router: Router,
        private readonly http: HttpClient,
        private readonly localStorage: GtthLocalStorageService,
        private readonly urlSerializer: UrlSerializer,
        private readonly suiNotificationService: SuiNotificationService
    ) {
        this.router.events
            .pipe(
                filter(
                    (event) =>
                        event instanceof NavigationEnd &&
                        event.urlAfterRedirects.indexOf('popup') === -1 &&
                        event.urlAfterRedirects !== '/' &&
                        !event.urlAfterRedirects.startsWith('/reset')
                )
            )
            .subscribe((navigationEvent: NavigationEnd) => {
                this.urlChanged(navigationEvent);
            });

        this.router.events.pipe(filter((event) => event instanceof NavigationStart && isDefined(this.notificationId))).subscribe(() => {
            this.suiNotificationService.closeById(this.notificationId);
            this.notificationId = undefined;
        });
    }

    tryRestore(): void {
        const convertToSessionNavigationModel = (sessionInfo: UserSessionRestoringDTO[]): Observable<GtthSessionNavigationModel[]> => {
            return from(sessionInfo).pipe(
                filter((info: UserSessionRestoringDTO) => info.url && info.url !== '/'),
                distinct((info: UserSessionRestoringDTO) => info.url),
                map((info: UserSessionRestoringDTO) => this.convertSessionInfoToNavigation(info)),
                toArray()
            );
        };

        const sessionInfo$ = this.http.get<UserSessionRestoringDTO[]>(this.API_URL);
        const sessionNavigationModel$: Observable<GtthSessionNavigationModel[]> = sessionInfo$.pipe(
            switchMap((sessionInfo) => convertToSessionNavigationModel(sessionInfo))
        );

        sessionNavigationModel$.subscribe((result: GtthSessionNavigationModel[]) => this.showPrompt(result));
    }

    private showPrompt(result: GtthSessionNavigationModel[]): void {
        if (!result.length) {
            return;
        }

        const navigationCommand = result.at(0).navigationCommand;
        if (this.router.url === navigationCommand) {
            return;
        }

        if (navigationCommand === this.navigationCommandHistory) {
            return;
        }

        this.navigationCommandHistory = navigationCommand;
        this.notificationId = this.suiNotificationService.showCustom(
            '',
            SuiNotificationType.INFO,
            UserSessionRestoringContentComponent,
            undefined,
            {
                isAutoClosable: false,
                data: result
            }
        );
    }

    private urlChanged(navigationEvent: NavigationEnd) {
        const activeUrls = this.localStorage.changeOpenTabUrl(navigationEvent.urlAfterRedirects);
        this.saveSessionInfo(activeUrls);
    }

    private saveSessionInfo(info: string[]): void {
        this.http
            .post(
                this.API_URL,
                info.map((url) => this.convertUrlToSessionInfoDto(url))
            )
            .subscribe();
    }

    private convertUrlToSessionInfoDto(url: string): UserSessionRestoringDTO {
        const urlTree = this.urlSerializer.parse(url);

        return {
            url,
            companyId: urlTree.queryParamMap.has(PARAM_CUSTOMER_ID) ? parseFloat(urlTree.queryParamMap.get(PARAM_CUSTOMER_ID)) : null,
            companyName: null,
            projectId: url.startsWith(this.projectViewUrl) ? parseFloat(url.slice(this.projectViewUrl.length, url.length)) : null,
            projectName: null
        } as UserSessionRestoringDTO;
    }

    private convertSessionInfoToNavigation(sessionInfo: UserSessionRestoringDTO): GtthSessionNavigationModel {
        const navigation = {
            navigationCommand: sessionInfo.url,
            moduleName: '',
            companyName: '',
            projectName: ''
        } as GtthSessionNavigationModel;

        let mainModulePath = sessionInfo.url;

        const paramsIndex = sessionInfo.url.indexOf(';');
        const queryParamsIndex = sessionInfo.url.indexOf('?');
        if (paramsIndex > 0 || queryParamsIndex > 0) {
            mainModulePath = mainModulePath.substring(0, paramsIndex > 0 ? paramsIndex : undefined);
            mainModulePath = mainModulePath.substring(0, queryParamsIndex > 0 ? queryParamsIndex : undefined);
        }

        const urlParams = this.urlSerializer.parse(sessionInfo.url);
        if (urlParams.queryParamMap.has(PARAM_PRODUCT_TYPE)) {
            mainModulePath += '.' + urlParams.queryParamMap.get(PARAM_PRODUCT_TYPE);
        } else if (mainModulePath.startsWith(this.projectViewUrl)) {
            mainModulePath = this.projectViewUrl.substring(0, this.projectViewUrl.length - 1);
        }
        navigation.moduleName = this.translationPrefix + mainModulePath.replace(/\//g, '.');

        if (sessionInfo.companyId && sessionInfo.companyName) {
            navigation.companyName = sessionInfo.companyName;
        }

        if (sessionInfo.projectId && sessionInfo.projectName) {
            navigation.projectName = sessionInfo.projectName;
        }

        return navigation;
    }
}
